'use client'

import {useEffect, useId, useMemo, useRef, useState} from 'react'
import {Search, X} from 'lucide-react'
import type {Contact} from '@/types'
import {displayName, filterContacts} from '@/lib/utils'
import {LazyAvatar} from '@/components/LazyAvatar'

const BLUR_CLOSE_MS = 200

type ContactPickerProps = {
    /** Full list to search in. */
    contacts: Contact[]
    /** Currently selected wxid. Empty string means nothing is selected. */
    value: string
    /** Called when the user picks a new contact (or clears the selection). */
    onChange: (wxid: string, contact: Contact | null) => void
    /** Visible placeholder when input is empty. */
    placeholder?: string
    /** Upper bound on dropdown rows. Defaults to unlimited. */
    limit?: number
    /** Max height for the floating dropdown (Tailwind class). */
    dropdownClassName?: string
    /** Extra class list for the outer wrapper (e.g. `w-full`). */
    className?: string
    /** If true, display the picker in a more compact layout (smaller avatars). */
    compact?: boolean
    /**
     * Optional chip renderer shown when a contact is selected. Defaults to
     * a "|avatar|name|wxid|X|" chip under the input.
     */
    renderSelected?: (contact: Contact | null, onClear: () => void) => React.ReactNode
    /** Empty-state text when the search has no matches. */
    emptyHint?: string
}

export function ContactPicker({
    contacts,
    value,
    onChange,
    placeholder = '输入昵称/备注/微信号搜索...',
    limit,
    dropdownClassName = 'max-h-60',
    className = 'w-full',
    compact = false,
    renderSelected,
    emptyHint = '没有匹配好友',
}: ContactPickerProps) {
    const [query, setQuery] = useState('')
    const [open, setOpen] = useState(false)
    const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const inputRef = useRef<HTMLInputElement | null>(null)
    const listboxId = useId()

    const selected = useMemo(
        () => contacts.find((contact) => contact.wxid === value) || null,
        [contacts, value],
    )

    const options = useMemo(() => {
        const filtered = filterContacts(contacts, query).filter(
            (contact) => contact.wxid !== value,
        )
        return typeof limit === 'number' ? filtered.slice(0, limit) : filtered
    }, [contacts, query, value, limit])

    useEffect(() => {
        return () => {
            if (blurTimer.current) {
                clearTimeout(blurTimer.current)
                blurTimer.current = null
            }
        }
    }, [])

    const handleSelect = (contact: Contact) => {
        setQuery('')
        setOpen(false)
        inputRef.current?.blur()
        onChange(contact.wxid, contact)
    }

    const handleClear = () => {
        setQuery('')
        setOpen(true)
        onChange('', null)
    }

    const avatarSize = compact ? 'w-7 h-7' : 'w-8 h-8'

    return (
        <div className={`relative ${className}`}>
            <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" size={14}/>
                <input
                    ref={inputRef}
                    type="text"
                    className="input pl-8 pr-8"
                    placeholder={placeholder}
                    value={query}
                    aria-expanded={open}
                    aria-controls={listboxId}
                    onFocus={() => {
                        if (blurTimer.current) {
                            clearTimeout(blurTimer.current)
                            blurTimer.current = null
                        }
                        setOpen(true)
                    }}
                    onBlur={() => {
                        blurTimer.current = setTimeout(() => {
                            setOpen(false)
                            blurTimer.current = null
                        }, BLUR_CLOSE_MS)
                    }}
                    onChange={(event) => {
                        setQuery(event.target.value)
                        setOpen(true)
                    }}
                />
                {query ? (
                    <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-ink p-0.5"
                        aria-label="清空搜索"
                        onClick={() => {
                            setQuery('')
                            setOpen(true)
                        }}
                    >
                        <X size={14}/>
                    </button>
                ) : null}
                {open ? (
                    <div
                        id={listboxId}
                        role="listbox"
                        className={`absolute z-20 w-full mt-1 bg-white border border-hair rounded-md shadow-card overflow-y-auto ${dropdownClassName}`}
                    >
                        {options.length ? (
                            options.map((contact) => (
                                <div
                                    key={contact.wxid}
                                    role="option"
                                    aria-selected={false}
                                    className="flex items-center p-2.5 hover:bg-page cursor-pointer border-b border-hair/40 last:border-b-0"
                                    onMouseDown={(event) => {
                                        event.preventDefault()
                                        handleSelect(contact)
                                    }}
                                >
                                    <LazyAvatar
                                        src={contact.avatar}
                                        seed={contact.wxid}
                                        alt={displayName(contact)}
                                        className={`${avatarSize} rounded mr-3 bg-gray-100 shrink-0`}
                                    />
                                    <div className="flex-1 truncate">
                                        <div className="text-sm font-medium text-ink truncate">
                                            {displayName(contact)}
                                        </div>
                                        <div className="text-xs text-muted truncate">{contact.wxid}</div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="p-3 text-sm text-muted text-center">{emptyHint}</div>
                        )}
                    </div>
                ) : null}
            </div>
            {selected
                ? renderSelected
                    ? renderSelected(selected, handleClear)
                    : (
                        <div className="flex items-center bg-page rounded-md p-2 gap-2 border border-hair mt-2">
                            <LazyAvatar
                                src={selected.avatar}
                                seed={selected.wxid}
                                alt={displayName(selected)}
                                className="w-8 h-8 rounded bg-gray-100 shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-ink truncate">{displayName(selected)}</div>
                                <div className="text-xs text-muted truncate">{selected.wxid}</div>
                            </div>
                            <button
                                type="button"
                                className="text-muted hover:text-red-500 p-1 rounded hover:bg-red-50 shrink-0"
                                onClick={handleClear}
                                aria-label="清空选择"
                            >
                                <X size={14}/>
                            </button>
                        </div>
                    )
                : null}
        </div>
    )
}
